import { toast } from 'sonner';
import { API_BASE_URL } from '@/utils/constants';
import { useAuthStore } from '@/stores/auth-store';
import { captureAPIError } from '@/shared/services/sentry';

/**
 * Backend HTTP client. Auto-injects:
 * - `Authorization: Bearer <accessToken>` when a session exists
 * - `X-Org-ID: <currentOrgId>` when an org is selected
 *
 * On 401, attempts a single refresh (single-flighted across concurrent calls);
 * if that fails, clears the session (router redirects to /login on next render).
 */

export class APIError extends Error {
  status: number;
  code: string;
  details?: unknown;
  requestId?: string;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

interface FetchOptions extends RequestInit {
  /** Set to `true` to skip auth headers (login/signup). */
  anonymous?: boolean;
  /** Skip the automatic refresh-and-retry on 401. Default: false. */
  noRetry?: boolean;
}

function buildHeaders(opts: FetchOptions): Headers {
  const headers = new Headers(opts.headers);
  if (!(opts.body instanceof FormData) && !headers.has('Content-Type') && opts.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }
  if (!opts.anonymous) {
    const { accessToken, currentOrgId } = useAuthStore.getState();
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    if (currentOrgId) headers.set('X-Org-ID', currentOrgId);
  }
  return headers;
}

async function parseError(response: Response): Promise<APIError> {
  const requestId = response.headers.get('X-Request-ID') ?? undefined;
  try {
    const data = await response.json();
    const err = data?.error ?? {};
    return new APIError(
      response.status,
      err.code ?? 'http_error',
      err.message ?? response.statusText,
      err.details,
      requestId,
    );
  } catch {
    return new APIError(response.status, 'http_error', response.statusText, undefined, requestId);
  }
}

let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const { setTokens, logout } = useAuthStore.getState();
    try {
      const r = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        credentials: 'include',
      });
      if (!r.ok) {
        await logout();
        return false;
      }
      const data = (await r.json()) as { access_token: string; refresh_token: string };
      setTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      await logout();
      return false;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  let response = await fetch(url, {
    ...opts,
    headers: buildHeaders(opts),
    credentials: 'include',
  });

  if (response.status === 401 && !opts.anonymous && !opts.noRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      response = await fetch(url, { ...opts, headers: buildHeaders(opts), credentials: 'include' });
    }
  }

  if (!response.ok) {
    const err = await parseError(response);
    if (response.status === 402 && err.code === 'payment_required') {
      notifyPaymentRequired(err.message);
    }
    if (response.status >= 500) {
      captureAPIError(err, { requestId: err.requestId, path });
    }
    throw err;
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Variant that returns the raw Response (for SSE / blob downloads). */
export async function apiStream(
  path: string,
  opts: FetchOptions = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  let response = await fetch(url, { ...opts, headers: buildHeaders(opts), credentials: 'include' });

  if (response.status === 401 && !opts.anonymous && !opts.noRetry) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      response = await fetch(url, { ...opts, headers: buildHeaders(opts), credentials: 'include' });
    }
  }

  if (!response.ok) {
    const err = await parseError(response);
    if (response.status === 402 && err.code === 'payment_required') {
      notifyPaymentRequired(err.message);
    }
    throw err;
  }
  return response;
}

// De-dupe 402 toasts when a page fires several requests at once.
let lastPaymentToastAt = 0;
function notifyPaymentRequired(message: string): void {
  const now = Date.now();
  if (now - lastPaymentToastAt < 4000) return;
  lastPaymentToastAt = now;
  toast.error(message, {
    duration: 6000,
    action: {
      label: 'Upgrade',
      onClick: () => {
        window.location.assign('/billing');
      },
    },
  });
}
